import { Delegate } from './utility/Delegate';
import { MiscUtil } from './utility/MiscUtil';
import { NormalizedEvent } from './event/NormalizedEvent';

type BooleanAction = (_: boolean) => void;
type EventAction = (_: NormalizedEvent, __: BooleanAction) => void;
type DismissDelegate = () => void;

/// <summary>
/// Global dispatcher of UI events such as FirstTouch, Press, Tap, LastTouch, including programmatically fired ones
/// like DismissTemporaryElements.
/// This is only a dispatcher - the event will not be canceled by this class
/// </summary>
class GlobalUIEvents {
  //// ===========================================================================================================
  //// Events
  //// ===========================================================================================================

  /// <summary>
  /// The Action is a callback you can use to cancel the event.  Pass true to preventDefault on the event when you
  /// invoke the callback.  Note that the callback must be synchronous.
  /// </summary>
  public static FirstTouch: Delegate<EventAction> = new Delegate<EventAction>();
  public static add_FirstTouch(ev: EventAction): void {
    this.FirstTouch.add(ev);
  }
  public static remove_FirstTouch(ev: EventAction): void {
    this.FirstTouch.remove(ev);
  }

  public static Press: Delegate<EventAction> = new Delegate<EventAction>();
  public static add_Press(ev: EventAction): void {
    this.Press.add(ev);
  }
  public static remove_Press(ev: EventAction): void {
    this.Press.remove(ev);
  }

  public static Tap: Delegate<EventAction> = new Delegate<EventAction>();
  public static add_Tap(ev: EventAction): void {
    this.Tap.add(ev);
  }
  public static remove_Tap(ev: EventAction): void {
    this.Tap.remove(ev);
  }

  public static LastTouch: Delegate<EventAction> = new Delegate<EventAction>();
  public static add_LastTouch(ev: EventAction): void {
    this.LastTouch.add(ev);
  }
  public static remove_LastTouch(ev: EventAction): void {
    this.LastTouch.remove(ev);
  }

  public static RightClick: Delegate<EventAction> = new Delegate<EventAction>();
  public static add_RightClick(ev: EventAction): void {
    this.RightClick.add(ev);
  }
  public static remove_RightClick(ev: EventAction): void {
    this.RightClick.remove(ev);
  }

  public static DragEnd: Delegate<EventAction> = new Delegate<EventAction>();
  public static add_DragEnd(ev: EventAction): void {
    this.DragEnd.add(ev);
  }
  public static remove_DragEnd(ev: EventAction): void {
    this.DragEnd.remove(ev);
  }

  /// <summary>
  /// Fired when some UI element has programmatically taken focus (in a conceptual sense, not necessarily becoming
  /// the active element). For example, this could be fired when a dialog appears via the JSAPI.
  /// Any UI element that is temporary and normally dismisses when the user touches outside of it (e.g. a tooltip)
  /// should usually listen for this event in addition to <see cref="FirstTouch"/>.
  /// </summary>
  public static DismissTemporaryElements: Delegate<DismissDelegate> = new Delegate<DismissDelegate>();
  public static add_DismissTemporaryElements(ev: DismissDelegate): void {
    this.DismissTemporaryElements.add(ev);
  }
  public static remove_DismissTemporaryElements(ev: DismissDelegate): void {
    this.DismissTemporaryElements.remove(ev);
  }

  //// ===========================================================================================================
  //// Methods
  //// ===========================================================================================================

  /// <summary>
  /// Fires <c>FirstTouch</c> event. As a side-effect, this also fires <c>DismissTemporaryElements</c> events
  /// </summary>
  public static FireFirstTouch(e: NormalizedEvent, cancelCallback: BooleanAction): void {
    GlobalUIEvents.FireEvent(GlobalUIEvents.FirstTouch, e, cancelCallback);
  }

  /// <summary>
  /// Fires <c>Press</c> event
  /// </summary>
  public static FirePress(e: NormalizedEvent, cancelCallback: BooleanAction): void {
    GlobalUIEvents.FireEvent(GlobalUIEvents.Press, e, cancelCallback);
  }

  /// <summary>
  /// Fires <c>Tap</c> event
  /// </summary>
  public static FireTap(e: NormalizedEvent, cancelCallback: BooleanAction): void {
    GlobalUIEvents.FireEvent(GlobalUIEvents.Tap, e, cancelCallback);
  }

  /// <summary>
  /// Fires <c>LastTouch</c> event
  /// </summary>
  public static FireLastTouch(e: NormalizedEvent, cancelCallback: BooleanAction): void {
    GlobalUIEvents.FireEvent(GlobalUIEvents.LastTouch, e, cancelCallback);
  }

  /// <summary>
  /// Fires <c>RightClick</c> event
  /// </summary>
  public static FireRightClick(e: NormalizedEvent, cancelCallback: BooleanAction): void {
    GlobalUIEvents.FireEvent(GlobalUIEvents.RightClick, e, cancelCallback);
  }

  /// <summary>
  /// Fires <c>DragEnd</c> event
  /// </summary>
  public static FireDragEnd(e: NormalizedEvent, cancelCallback: BooleanAction): void {
    GlobalUIEvents.FireEvent(GlobalUIEvents.DragEnd, e, cancelCallback);
  }

  /// <summary>
  /// Fires <c>DismissTemporaryElements</c> event
  /// </summary>
  public static FireDismissTemporaryElements(): void {
    if (!MiscUtil.IsNullOrUndefined(GlobalUIEvents.DismissTemporaryElements)) {
      GlobalUIEvents.DismissTemporaryElements.invoke(null);
    }
  }

  /// <summary>
  /// Helper function to fire event.
  /// </summary>
  private static FireEvent(
    ev: Delegate<EventAction>,
    eventArgument: NormalizedEvent,
    cancelCallback: BooleanAction): void {
    if (!MiscUtil.IsNullOrUndefined(ev)) {
      ev.invoke(eventArgument, cancelCallback);
    }
  }
}

export { BooleanAction, EventAction, DismissDelegate, GlobalUIEvents };

